// src/components/TeamManagement.js
import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebaseConfig'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  writeBatch,
  orderBy,
  updateDoc,
  deleteField,
  getDocs
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next'; 

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography, 
  CircularProgress, Paper, Divider, Button, TextField, List,
  ListItem, ListItemText, ListItemIcon, Select, MenuItem,
  FormControl, InputLabel, Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemButton,
  FormHelperText,
  Tooltip,
  IconButton,
  InputAdornment
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LockResetIcon from '@mui/icons-material/LockReset';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
// --- FINE IMPORT MUI ---


// --- Componente Upload Singolo Logo ---
const LogoUploader = ({ teamId, isOwner, label, helperText, storagePath, dbField, initialUrl, previewBg }) => {
  const { t } = useTranslation('team');
  const [logoUrl, setLogoUrl] = useState(initialUrl || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFeedback, setUploadFeedback] = useState('');

  useEffect(() => {
    setLogoUrl(initialUrl || '');
  }, [initialUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) { 
      setUploadFeedback(t('logo_error_size')); 
      return;
    }

    setIsUploading(true);
    setUploadFeedback('');
    const logoRef = ref(storage, `team_logos/${teamId}/${storagePath}`); 

    try {
      await uploadBytes(logoRef, file);
      const url = await getDownloadURL(logoRef);
      await updateDoc(doc(db, "teams", teamId), { [dbField]: url });
      setLogoUrl(url); 
      setUploadFeedback(t('logo_success'));
      setTimeout(() => setUploadFeedback(''), 3000);
    } catch (error) {
      console.error(`Error uploading ${dbField}:`, error);
      setUploadFeedback(t('logo_error_upload'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ flex: 1, minWidth: '250px' }}>
      <Typography variant="h6" gutterBottom>{label}</Typography>
      
      {/* Anteprima */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: previewBg,
          width: '100%', 
          height: 120, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 2
        }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={t('logo_alt')} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <Typography variant="body2" color={previewBg === '#FFFFFF' ? 'text.disabled' : 'text.secondary'}>
            {t('logo_no_preview')}
          </Typography>
        )}
      </Paper>

      {/* Pulsante e Feedback */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          component="label"
          disabled={isUploading || !isOwner}
          size="small"
        >
          {isUploading ? <CircularProgress size={20} /> : t('logo_button')}
          <input
            type="file"
            hidden
            accept="image/png, image/jpeg, image/webp"
            onChange={handleFileSelect}
          />
        </Button>
        {uploadFeedback && (
          <Alert 
            severity={uploadFeedback === t('logo_success') ? 'success' : 'error'} 
            sx={{ p: '0px 12px', flex: 1 }}
          >
            {uploadFeedback}
          </Alert>
        )}
      </Box>
      <FormHelperText>{helperText}</FormHelperText>
    </Box>
  );
};


// --- Componente TeamDetailsForm (MODIFICATO) ---
const TeamDetailsForm = ({ teamData, teamId, isOwner, isLoading }) => {
  const { t } = useTranslation('team'); 
  const [name, setName] = useState(teamData?.nomeTeam || '');
  const [address, setAddress] = useState(teamData?.address || '');
  const [city, setCity] = useState(teamData?.city || '');
  const [piva, setPiva] = useState(teamData?.piva || '');
  const [costoOrario, setCostoOrario] = useState(teamData?.costoOrario || '');
  const [pitLaneBuffer, setPitLaneBuffer] = useState(teamData?.pitLaneBuffer || 0);
  
  // Rimosso state logoUrl, isUploading, uploadFeedback (ora gestiti dai figli)

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  useEffect(() => {
    setName(teamData?.nomeTeam || '');
    setAddress(teamData?.address || '');
    setCity(teamData?.city || '');
    setPiva(teamData?.piva || '');
    setCostoOrario(teamData?.costoOrario || '');
    setPitLaneBuffer(teamData?.pitLaneBuffer || 0);
    // Non serve più settare logoUrl qui
  }, [teamData]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isOwner || isSaving) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await updateDoc(doc(db, "teams", teamId), {
        nomeTeam: name.trim(),
        address: address.trim(),
        city: city.trim(),
        piva: piva.trim(),
        costoOrario: Number(costoOrario) || 0,
        pitLaneBuffer: Number(pitLaneBuffer) || 0
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving team data:", error);
      alert("Unable to save team data."); 
    } finally {
      setIsSaving(false);
    }
  };

  // Rimossa handleFileSelect (ora gestita dai figli)

  return (
    <Paper 
      elevation={0} 
      sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
    >
      <Typography variant="h5" gutterBottom>{t('details_title')}</Typography>
      {isOwner ? (
        <Box component="form" onSubmit={handleSave}>
          {/* Campi Dati (Nome, PIVA, Indirizzo, Città) */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField label={t('team_name')} value={name} onChange={(e) => setName(e.target.value)} required fullWidth sx={{ flex: 1 }} />
            <TextField label={t('vat_code')} value={piva} onChange={(e) => setPiva(e.target.value)} fullWidth sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2.5 }}>
            <TextField label={t('address')} value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
            <TextField label={t('city')} value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
          </Box>
          
          <Divider sx={{ my: 3 }} />

          {/* --- MODIFICA: Sezione Upload Logo con Doppia Anteprima --- */}
          <Typography variant="h5" gutterBottom>{t('logo_title')}</Typography>
          <FormHelperText sx={{ mb: 2.5, mt: -1 }}>{t('logo_helper_main')}</FormHelperText>

          <Box sx={{ display: 'flex', gap: 3, mb: 2.5, flexWrap: 'wrap' }}>
            {/* Uploader 1: Tema Chiaro */}
            <LogoUploader
              teamId={teamId}
              isOwner={isOwner}
              label={t('logo_title_light')}
              helperText={t('logo_helper_light')}
              storagePath="logo_light"
              dbField="logoUrlLight"
              initialUrl={teamData?.logoUrlLight}
              previewBg="#FFFFFF" // Sfondo chiaro forzato
            />
            
            {/* Uploader 2: Tema Scuro */}
            <LogoUploader
              teamId={teamId}
              isOwner={isOwner}
              label={t('logo_title_dark')}
              helperText={t('logo_helper_dark')}
              storagePath="logo_dark"
              dbField="logoUrlDark"
              initialUrl={teamData?.logoUrlDark}
              previewBg="background.default" // Sfondo del tema
            />
          </Box>
          {/* --- Fine Sezione Logo --- */}

          <Divider sx={{ my: 3 }} />

          {/* Campi Costo Orario e Buffer (invariati) */}
          <Typography variant="h6" gutterBottom>{t('billing_settings_title')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField 
              label={t('hourly_rate')}
              type="number" value={costoOrario} 
              onChange={(e) => setCostoOrario(e.target.value)} 
              fullWidth sx={{ flex: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                endAdornment: <InputAdornment position="end">/hour</InputAdornment>,
                inputProps: { step: "0.01", min: "0" }
              }}
              helperText={t('hourly_rate_helper')}
            />
            <TextField 
              label={t('pitlane_buffer')}
              type="number" value={pitLaneBuffer} 
              onChange={(e) => setPitLaneBuffer(e.target.value)} 
              fullWidth sx={{ flex: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
                inputProps: { min: "0" }
              }}
              helperText={t('pitlane_buffer_helper')}
            />
          </Box>

          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={isSaving || !isOwner || isLoading} 
            sx={{ mt: 2, height: '56px' }}
            size="large"
          >
            {isSaving ? <CircularProgress size={20} color="inherit" /> : t('save_button')}
          </Button>
          {saveSuccess && <Alert severity="success" sx={{ mt: 2 }}>{t('save_success')}</Alert>}
        </Box>
      ) : (
         <Alert severity="info">{t('admin_only')}</Alert>
      )}
    </Paper>
  );
};
// --- Fine Componente TeamDetailsForm ---


//
// --- IL RESTO DEL FILE TeamManagement.js RIMANE INVARIATO ---
// (Funzione TeamManagement, useEffects, Handlers, Render...)
//

function TeamManagement({ teamId, user, userRole }) {
  const { t } = useTranslation('team');
  // Stati (Invariati)
  const [teamData, setTeamData] = useState(null);
  const [members, setMembers] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false); 
  const [currentUserForDialog, setCurrentUserForDialog] = useState(null); 
  const [selectedVehiclesInDialog, setSelectedVehiclesInDialog] = useState([]); 
  const [isSavingAssignments, setIsSavingAssignments] = useState(false); 
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [currentUserForRoleChange, setCurrentUserForRoleChange] = useState(null);
  const [newRoleInModal, setNewRoleInModal] = useState('');
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [currentUserForNameChange, setCurrentUserForNameChange] = useState(null);
  const [newNameInModal, setNewNameInModal] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const isOwner = userRole === 'admin';

  // --- useEffects (Invariati) ---
  useEffect(() => {
    if (!teamId) return;
    setIsLoadingTeam(true);
    const teamDocRef = doc(db, 'teams', teamId);
    let unsubUsers = null; 
    const unsubTeam = onSnapshot(teamDocRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            setTeamData(data);
            const memberRolesMap = data.membri || {}; 
            const memberUids = Object.keys(memberRolesMap); 
            if (memberUids.length === 0) {
                setMembers([]);
                setIsLoadingTeam(false);
                return;
            }
            if (unsubUsers) unsubUsers();
            const usersQuery = query(collection(db, "users"), where("teamId", "==", teamId));
            unsubUsers = onSnapshot(usersQuery, (usersSnap) => {
                const usersMap = {};
                usersSnap.forEach(userDoc => {
                    usersMap[userDoc.id] = userDoc.data();
                });
                const membersArray = memberUids.map(uid => ({
                    uid: uid,
                    email: usersMap[uid]?.email || `Email not found (UID: ${uid.substring(0, 6)}...)`, 
                    nomeCompleto: usersMap[uid]?.nomeCompleto || '',
                    role: memberRolesMap[uid] || 'N/A'
                }));
                setMembers(membersArray);
                setIsLoadingTeam(false); 
            }, (err) => {
                console.error("Error loading users:", err);
                const membersArray = memberUids.map(uid => ({
                    uid: uid, 
                    email: `User ${uid.substring(0, 6)}... (Error)`, 
                    role: memberRolesMap[uid] || 'N/A'
                }));
                setMembers(membersArray);
                setIsLoadingTeam(false);
            });
        } else {
            setIsLoadingTeam(false);
        }
    }, (err) => {
        console.error("Error loading team:", err);
        setIsLoadingTeam(false);
    });
    return () => {
      unsubTeam();
      if (unsubUsers) unsubUsers();
    };
  }, [teamId]);



  // --- CARICA RICHIESTE DI ACCESSO ---
  useEffect(() => {
    if (!teamId) return;
    setIsLoadingRequests(true);
    const q = query(collection(db, "teamRequests"), where("teamId", "==", teamId), where("status", "==", "pending"));
    const unsub = onSnapshot(q, (qs) => {
      setTeamRequests(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingRequests(false);
    }, (err) => { console.error(err); setIsLoadingRequests(false); });
    return () => unsub();
  }, [teamId]);

  // --- AUTO-GENERA CODICE PER VECCHI TEAM ---
  useEffect(() => {
    if (teamData && isOwner && !teamData.teamCode) {
      const generateCode = async () => {
         const code = Math.random().toString(36).substring(2, 8).toUpperCase();
         try {
           await updateDoc(doc(db, 'teams', teamId), { teamCode: code, maxMembers: 50 });
         } catch (e) {
           console.error("Error generating backward compatibility code", e);
         }
      };
      generateCode();
    }
  }, [teamData, isOwner, teamId]);

  useEffect(() => {
    if (!teamId) return;
    setIsLoadingVehicles(true);
    setIsLoadingAssignments(true);
    const vehiclesQuery = query(collection(db, "vehicles"), where("teamId", "==", teamId), orderBy("marca"));
    const unsubVehicles = onSnapshot(vehiclesQuery, (qs) => {
      setVehicles(qs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingVehicles(false);
    }, (err) => { console.error("Error loading vehicles:", err); setIsLoadingVehicles(false); });
    const assignQuery = query(collection(db, "vehicleAssignments"), where("teamId", "==", teamId));
    const unsubAssignments = onSnapshot(assignQuery, (qs) => {
      const assignmentsMap = {};
      qs.docs.forEach(doc => {
        const data = doc.data();
        if (!assignmentsMap[data.userId]) { assignmentsMap[data.userId] = []; }
        assignmentsMap[data.userId].push(data.vehicleId);
      });
      setAssignments(assignmentsMap);
      setIsLoadingAssignments(false);
    }, (err) => { console.error("Error loading assignments:", err); setIsLoadingAssignments(false); });
    return () => { unsubVehicles(); unsubAssignments(); };
  }, [teamId]);
  // --- FINE useEffects ---


  // --- Funzioni Handler (Invariate) ---
  
  const handleOpenAssignDialog = (member) => {
    setCurrentUserForDialog(member);
    const currentAssignments = assignments[member.uid] || [];
    setSelectedVehiclesInDialog(currentAssignments);
    setDialogOpen(true);
  };
  
  const handleCloseAssignDialog = () => {
    setDialogOpen(false); 
    setIsSavingAssignments(false);
    setCurrentUserForDialog(null); 
    setSelectedVehiclesInDialog([]);
  };

  const handleToggleVehicleCheckbox = (vehicleId) => {
    setSelectedVehiclesInDialog((prev) =>
      prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]
    );
  };
  
  const handleSaveAssignments = async () => {
    if (!currentUserForDialog) return;
    setIsSavingAssignments(true);
    const userId = currentUserForDialog.uid;
    const oldAssignments = assignments[userId] || [];
    const newAssignments = selectedVehiclesInDialog;
    const toAdd = newAssignments.filter(id => !oldAssignments.includes(id));
    const toRemove = oldAssignments.filter(id => !newAssignments.includes(id));

    try {
      const batch = writeBatch(db);
      toAdd.forEach(vehicleId => {
        const docId = `${userId}_${vehicleId}`;
        const ref = doc(db, "vehicleAssignments", docId);
        batch.set(ref, { teamId, userId, vehicleId });
      });
      toRemove.forEach(vehicleId => {
        const docId = `${userId}_${vehicleId}`;
        const ref = doc(db, "vehicleAssignments", docId);
        batch.delete(ref);
      });
      await batch.commit();
      handleCloseAssignDialog();
    } catch (error) {
      console.error("Error saving assignments:", error);
      alert("Error while saving assignments.");
      setIsSavingAssignments(false);
    }
  };
  
  const handlePasswordReset = (emailToReset) => {
    if (!emailToReset) {
      alert("Error: Email not found for this user.");
      return;
    }
    const isCurrentUser = user.email === emailToReset;
    const confirmMsg = isCurrentUser
      ? t('reset_confirm_you')
      : t('reset_confirm_other', { email: emailToReset });
      
    if (window.confirm(confirmMsg)) {
      sendPasswordResetEmail(auth, emailToReset)
        .then(() => {
          alert(t('reset_success', { email: emailToReset }));
        })
        .catch((error) => {
          console.error("Error sending password reset email:", error);
          alert(`Error: ${error.message}`);
        });
    }
  };

  const handleOpenRoleModal = (member) => {
    setCurrentUserForRoleChange(member);
    setNewRoleInModal(member.role); 
    setIsRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setIsRoleModalOpen(false);
    setIsSavingRole(false);
    setCurrentUserForRoleChange(null);
    setNewRoleInModal('');
  };

  const handleUpdateRole = async () => {
    if (!currentUserForRoleChange || !newRoleInModal) return;
    
    setIsSavingRole(true);
    try {
      const teamDocRef = doc(db, 'teams', teamId);
      await updateDoc(teamDocRef, {
        [`membri.${currentUserForRoleChange.uid}`]: newRoleInModal
      });
      handleCloseRoleModal();
    } catch (error) {
      console.error("Error updating role:", error);
      alert(t('role_update_error')); 
      setIsSavingRole(false);
    }
  };

  const handleOpenNameModal = (member) => {
    setCurrentUserForNameChange(member);
    setNewNameInModal(member.nomeCompleto || '');
    setIsNameModalOpen(true);
  };

  const handleCloseNameModal = () => {
    setIsNameModalOpen(false);
    setIsSavingName(false);
    setCurrentUserForNameChange(null);
    setNewNameInModal('');
  };

  const handleUpdateName = async () => {
    if (!currentUserForNameChange) return;
    setIsSavingName(true);
    try {
      const userRef = doc(db, 'users', currentUserForNameChange.uid);
      await updateDoc(userRef, { nomeCompleto: newNameInModal });
      handleCloseNameModal();
    } catch (error) {
      console.error("Error updating user name:", error);
      alert(t('update_name_error', { defaultValue: "Errore durante l'aggiornamento del nome utente." }));
      setIsSavingName(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (window.confirm(t('remove_confirm', { email: member.email }))) { 
      try {
        const batch = writeBatch(db);
        
        const teamDocRef = doc(db, 'teams', teamId);
        batch.update(teamDocRef, {
          [`membri.${member.uid}`]: deleteField() 
        });
        
        const userDocRef = doc(db, 'users', member.uid);
        batch.update(userDocRef, {
          teamId: null
        });
        
        const assignQuery = query(collection(db, "vehicleAssignments"), where("teamId", "==", teamId), where("userId", "==", member.uid));
        const assignSnap = await getDocs(assignQuery); 
        assignSnap.forEach(doc => {
            batch.delete(doc.ref);
        });

        await batch.commit();
        alert(t('remove_success', { email: member.email })); 
      } catch (error) {
        console.error("Error removing member:", error);
        alert(t('remove_error')); 
      }
    }
  };

  // --- GESTIONE RICHIESTE ---
  const [requestRoleModalOpen, setRequestRoleModalOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [requestRole, setRequestRole] = useState('Mechanic');

  const handleOpenRequestModal = (request) => {
    setCurrentRequest(request);
    setRequestRole('Mechanic');
    setRequestRoleModalOpen(true);
  };

  const handleApproveRequest = async () => {
    if (!currentRequest) return;
    
    // Controllo limite membri
    const memberCount = Object.keys(teamData.membri || {}).length;
    const maxAllowed = teamData.maxMembers || 50;
    
    if (memberCount >= maxAllowed) {
      alert(`Hai raggiunto il limite massimo di ${maxAllowed} membri. Non puoi approvare altri utenti.`);
      return;
    }

    try {
      const batch = writeBatch(db);
      
      const teamRef = doc(db, 'teams', teamId);
      batch.update(teamRef, {
        [`membri.${currentRequest.userId}`]: requestRole
      });
      
      const userRef = doc(db, 'users', currentRequest.userId);
      batch.update(userRef, { teamId: teamId });
      
      const requestRef = doc(db, 'teamRequests', currentRequest.id);
      batch.update(requestRef, { status: 'approved' });
      
      await batch.commit();
      
      setRequestRoleModalOpen(false);
      setCurrentRequest(null);
    } catch (error) {
      console.error("Errore approvazione richiesta:", error);
      alert("Si è verificato un errore durante l'approvazione.");
    }
  };
  
  const handleRejectRequest = async (requestId) => {
    if (window.confirm("Sei sicuro di voler rifiutare questa richiesta?")) {
      try {
        await updateDoc(doc(db, 'teamRequests', requestId), { status: 'rejected' });
      } catch (e) {
        console.error(e);
      }
    }
  };
  // --- FINE Funzioni Handler ---


  if (isLoadingTeam) { return ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box> ); }
  if (!teamData) { return <Alert severity="error">Error: Unable to load team data.</Alert>; }


  // --- RENDER (Invariato) ---
  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')} 
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {teamData.nomeTeam}
        </Typography>
        {isOwner && teamData.teamCode && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, display: 'inline-block' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Codice d'Accesso del Team (da condividere con i nuovi membri):
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: '2px', color: 'primary.main' }}>
              {teamData.teamCode}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Membri attuali: {Object.keys(teamData.membri || {}).length} / {teamData.maxMembers || 50}
            </Typography>
          </Box>
        )}
      </Paper>
        
      <TeamDetailsForm teamData={teamData} teamId={teamId} isOwner={isOwner} isLoading={isLoadingTeam} />
      
      <Paper 
        elevation={0} 
        sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>{t('members_title')}</Typography>
          {(isLoadingTeam || isLoadingAssignments) ? <CircularProgress size={24} /> : (
            <List dense>
              {members.length > 0 ? (
                members.map((member) => {
                  const memberAssignments = assignments[member.uid] || [];
                  const assignedCount = memberAssignments.length;
                  const isSelf = member.uid === user.uid;
                  
                  let roleIcon = <PersonIcon />;
                  if (member.role === 'Guest') roleIcon = <VisibilityIcon />;
                  if (member.role === 'admin') roleIcon = <ManageAccountsIcon />;
                  
                  return (
                    <ListItem
                      key={member.uid}
                      divider
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {isOwner && !isSelf && (
                            <>
                              <Tooltip title={t('tooltip_assign')}>
                                <IconButton size="small" onClick={() => handleOpenAssignDialog(member)} disabled={isLoadingVehicles}>
                                  <DirectionsCarIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('tooltip_role')}>
                                <IconButton size="small" onClick={() => handleOpenRoleModal(member)}>
                                  <ManageAccountsIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('tooltip_edit_name', { defaultValue: 'Modifica Nome Pilota/Membro' })}>
                                <IconButton size="small" onClick={() => handleOpenNameModal(member)}>
                                  <PersonIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('tooltip_remove')}>
                                <IconButton size="small" onClick={() => handleRemoveMember(member)}>
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {(isOwner || isSelf) && (
                            <Tooltip title={isSelf ? t('tooltip_reset_you') : t('tooltip_reset_other')}>
                              <IconButton size="small" color="secondary" onClick={() => handlePasswordReset(member.email)}>
                                <LockResetIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      }
                    >
                      <ListItemIcon>{roleIcon}</ListItemIcon> 
                      <ListItemText
                        primary={member.nomeCompleto ? `${member.nomeCompleto} (${member.email}) ${isSelf ? t('you') : ''}` : `${member.email} ${isSelf ? t('you') : ''}`}
                        secondary={
                          (member.role === 'admin' || member.role === 'Guest')
                            ? (member.role === 'admin' ? t('role_admin_access') : t('role_guest_access')) 
                            : `${member.role} | ${t('role_access_prefix')} ${assignedCount} ${t('role_access_suffix')}`
                        }
                      />
                    </ListItem>
                  );
                })
              ) : ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{t('no_members')}</Typography> )}
            </List>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        
        {isOwner && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>Richieste d'Accesso in Sospeso</Typography>
            {isLoadingRequests ? <CircularProgress size={20} /> : (
              <List dense>
                {teamRequests.length > 0 ? (
                  teamRequests.map((req) => (
                    <ListItem 
                      key={req.id} 
                      divider
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="contained" color="primary" onClick={() => handleOpenRequestModal(req)}>
                            Approva
                          </Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => handleRejectRequest(req.id)}>
                            Rifiuta
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText primary={req.email} secondary="Vuole unirsi al team" />
                    </ListItem>
                  ))
                ) : ( <Typography variant="body2" sx={{ fontStyle: 'italic' }}>Nessuna richiesta in sospeso</Typography> )}
              </List>
            )}
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
      </Paper>
      
      <Dialog open={dialogOpen} onClose={handleCloseAssignDialog} fullWidth maxWidth="xs">
        <DialogTitle>{t('dialog_assign_title', { email: currentUserForDialog?.email || '...' })}</DialogTitle>
        <DialogContent>
          {isLoadingVehicles ? <CircularProgress /> : (
            <List dense>
              {vehicles.length === 0 && <Typography sx={{p:2, fontStyle: 'italic'}}>{t('dialog_assign_empty')}</Typography>}
              {vehicles.map((vehicle) => {
                const isChecked = selectedVehiclesInDialog.includes(vehicle.id);
                return (
                  <ListItem key={vehicle.id} disablePadding>
                    <ListItemButton onClick={() => handleToggleVehicleCheckbox(vehicle.id)}>
                      <ListItemIcon>
                        <Checkbox edge="start" checked={isChecked} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemText 
                        primary={`${vehicle.marca} ${vehicle.modello}`} 
                        secondary={`Nr. ${vehicle.numeroGara || 'N/A'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog} disabled={isSavingAssignments}>{t('dialog_cancel')}</Button>
          <Button onClick={handleSaveAssignments} variant="contained" disabled={isSavingAssignments}>
            {isSavingAssignments ? <CircularProgress size={24} /> : t('dialog_assign_save')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isRoleModalOpen} onClose={handleCloseRoleModal} fullWidth maxWidth="xs">
        <DialogTitle>{t('dialog_role_title', { email: currentUserForRoleChange?.email || '...' })}</DialogTitle>
        <DialogContent sx={{pt: '20px !important'}}>
            <FormControl fullWidth disabled={isSavingRole}>
              <InputLabel id="role-change-label">{t('dialog_role_new')}</InputLabel>
              <Select 
                labelId="role-change-label" 
                value={newRoleInModal} 
                label={t('dialog_role_new')}
                onChange={(e) => setNewRoleInModal(e.target.value)}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="Engineer">Engineer</MenuItem>
                <MenuItem value="Mechanic">Mechanic</MenuItem>
                <MenuItem value="Driver">Pilota</MenuItem>
                <MenuItem value="Magazziniere">Storekeeper</MenuItem>
                <MenuItem value="Guest">Guest</MenuItem>
              </Select>
            </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleModal} disabled={isSavingRole}>{t('dialog_cancel')}</Button>
          <Button onClick={handleUpdateRole} variant="contained" disabled={!newRoleInModal || isSavingRole}>
            {isSavingRole ? <CircularProgress size={24} /> : t('role_modal_save')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isNameModalOpen} onClose={handleCloseNameModal} fullWidth maxWidth="xs">
        <DialogTitle>{t('edit_name_modal_title', { defaultValue: 'Modifica Nome Reale Membro/Pilota' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('edit_name_modal_subtitle', { defaultValue: 'Assegna un Nome/Cognome reale...', email: currentUserForNameChange?.email })}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('edit_name_label', { defaultValue: 'Nome Completo Reale' })}
            fullWidth
            variant="outlined"
            value={newNameInModal}
            onChange={(e) => setNewNameInModal(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNameModal} disabled={isSavingName}>{t('dialog_cancel')}</Button>
          <Button onClick={handleUpdateName} variant="contained" disabled={isSavingName}>
            {isSavingName ? <CircularProgress size={24} /> : t('save_name', { defaultValue: 'Salva Nome' })}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={requestRoleModalOpen} onClose={() => setRequestRoleModalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Approva e Assegna Ruolo</DialogTitle>
        <DialogContent sx={{pt: '20px !important'}}>
          <Typography variant="body2" sx={{ mb: 2 }}>Assegna un ruolo a {currentRequest?.email}</Typography>
          <FormControl fullWidth>
            <InputLabel>Ruolo</InputLabel>
            <Select 
              value={requestRole} 
              label="Ruolo"
              onChange={(e) => setRequestRole(e.target.value)}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="Engineer">Engineer</MenuItem>
              <MenuItem value="Mechanic">Mechanic</MenuItem>
              <MenuItem value="Driver">Pilota</MenuItem>
              <MenuItem value="Magazziniere">Storekeeper</MenuItem>
              <MenuItem value="Guest">Guest</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestRoleModalOpen(false)}>Annulla</Button>
          <Button onClick={handleApproveRequest} variant="contained">Conferma Approvazione</Button>
        </DialogActions>
      </Dialog>
      
    </>
  );
}


export default TeamManagement;